import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
    LineChart, MapPin, Phone, MessageCircle, FileSignature, Search, RefreshCw,
    AlertTriangle, CheckCircle2, ExternalLink, BarChart3, Smartphone, Loader2,
    TrendingUp, Globe2, Settings as SettingsIcon,
} from "lucide-react";

/**
 * Admin SEO Insights — combines GA4 + Google Search Console.
 * Mobile-first, conversion-focused (not a generic analytics dump).
 *
 * Sections:
 *   1. Setup status banner (graceful degradation)
 *   2. Headline KPIs (top page, top query, total leads, total clicks)
 *   3. Conversion-driving pages (form_submit / phone_click / whatsapp_click events)
 *   4. Top SEO pages (impressions / clicks / CTR / position from GSC)
 *   5. Top keywords (from GSC) — with CTR and position
 *   6. City-wise sessions (from GA4 geo)
 *   7. Device split + cache refresh
 */

function StatusBanner({ status, onConfigure }) {
    if (!status) return null;
    if (status.configured) {
        return (
            <div className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                <span><span className="font-semibold">SEO Insights live.</span> GA4 + Search Console connected.</span>
            </div>
        );
    }
    const missing = [];
    if (!status.ga4_configured) missing.push("GA4");
    if (!status.gsc_configured) missing.push("Search Console");
    return (
        <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900" data-testid="seo-setup-banner">
            <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-amber-600" />
                <div className="flex-1 min-w-0">
                    <div className="font-semibold">Set up {missing.join(" + ")} to unlock this dashboard</div>
                    <div className="text-sm mt-1">Paste your Google service-account JSON, GA4 Property ID and Search Console site URL in <strong>Site Settings → SEO Insights</strong>. Read-only access — no changes to your Google data.</div>
                    {status.error && <div className="text-xs mt-1 text-red-700">{status.error}</div>}
                    <button
                        onClick={onConfigure}
                        data-testid="seo-go-configure"
                        className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-900 bg-white border border-amber-300 px-3 py-1.5 rounded-md hover:bg-amber-100"
                    >
                        <SettingsIcon className="w-3.5 h-3.5" /> Open Site Settings
                    </button>
                </div>
            </div>
        </div>
    );
}

function MetricTile({ icon: Icon, label, value, sub, accent = "blue" }) {
    const accentMap = {
        blue: "bg-[#1E3A8A]/5 text-[#1E3A8A] border-[#1E3A8A]/15",
        amber: "bg-amber-50 text-amber-700 border-amber-200",
        emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
        rose: "bg-rose-50 text-rose-700 border-rose-200",
    };
    return (
        <div className={`p-4 rounded-2xl border ${accentMap[accent]}`}>
            <div className="flex items-center gap-2 mb-1">
                <Icon className="w-4 h-4" />
                <div className="text-[10px] font-bold uppercase tracking-[0.18em]">{label}</div>
            </div>
            <div className="font-heading font-bold text-2xl text-[#0F172A] truncate" title={value}>{value || "—"}</div>
            {sub && <div className="text-xs text-slate-500 mt-1 truncate">{sub}</div>}
        </div>
    );
}

function SectionCard({ title, eyebrow, children, action, testId }) {
    return (
        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={testId}>
            <div className="flex items-start justify-between gap-3 mb-4">
                <div className="min-w-0">
                    {eyebrow && <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#3B82F6] mb-1">{eyebrow}</div>}
                    <h3 className="font-heading font-semibold text-[#0F172A] text-base sm:text-lg truncate">{title}</h3>
                </div>
                {action}
            </div>
            {children}
        </div>
    );
}

function EmptyState({ message }) {
    return (
        <div className="text-center py-8 text-sm text-slate-500" data-testid="seo-empty-state">
            {message || "No data yet — Google typically reports back within 24-48 hours of activity."}
        </div>
    );
}

function LoadingRow() {
    return (
        <div className="flex items-center justify-center py-8 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" />
        </div>
    );
}

const DAYS_OPTIONS = [
    { v: 7, l: "Last 7 days" },
    { v: 28, l: "Last 28 days" },
    { v: 90, l: "Last 90 days" },
];

export default function SeoInsightsPanel({ onNavigate }) {
    const [days, setDays] = useState(28);
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState({});
    const [topPages, setTopPages] = useState([]);
    const [conversions, setConversions] = useState([]);
    const [cityStats, setCityStats] = useState([]);
    const [device, setDevice] = useState({});
    const [gscQueries, setGscQueries] = useState([]);
    const [gscPages, setGscPages] = useState([]);
    const [refreshing, setRefreshing] = useState(false);

    const setLoad = (k, v) => setLoading((s) => ({ ...s, [k]: v }));

    const fetchAll = useCallback(async () => {
        setLoad("status", true);
        try {
            const r = await api.get("/admin/seo/status");
            setStatus(r.data);
        } catch {
            setStatus({ configured: false });
        } finally {
            setLoad("status", false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    useEffect(() => {
        if (!status?.ga4_configured && !status?.gsc_configured) return;
        const params = `?days_back=${days}`;
        if (status?.ga4_configured) {
            setLoad("ga4", true);
            Promise.all([
                api.get(`/admin/seo/ga4/top-pages${params}&limit=15`).then((r) => setTopPages(r.data?.data || [])).catch(() => setTopPages([])),
                api.get(`/admin/seo/ga4/conversions${params}`).then((r) => setConversions(r.data?.data || [])).catch(() => setConversions([])),
                api.get(`/admin/seo/ga4/sessions-by-city${params}&limit=15`).then((r) => setCityStats(r.data?.data || [])).catch(() => setCityStats([])),
                api.get(`/admin/seo/ga4/device-split${params}`).then((r) => setDevice(r.data?.data || {})).catch(() => setDevice({})),
            ]).finally(() => setLoad("ga4", false));
        }
        if (status?.gsc_configured) {
            setLoad("gsc", true);
            Promise.all([
                api.get(`/admin/seo/gsc/top-queries${params}&limit=20`).then((r) => setGscQueries(r.data?.data || [])).catch(() => setGscQueries([])),
                api.get(`/admin/seo/gsc/top-pages${params}&limit=20`).then((r) => setGscPages(r.data?.data || [])).catch(() => setGscPages([])),
            ]).finally(() => setLoad("gsc", false));
        }
    }, [status, days]);

    const refresh = async () => {
        setRefreshing(true);
        try {
            await api.post("/admin/seo/cache/refresh");
            toast.success("SEO cache cleared — fresh data loading…");
            await fetchAll();
        } catch (e) {
            toast.error("Refresh failed");
        } finally {
            setRefreshing(false);
        }
    };

    // ---------- KPI roll-ups (computed from fetched data) ----------
    const totalConversions = conversions.reduce((sum, c) => sum + (c.event_count || 0), 0);
    const conversionsByEvent = conversions.reduce((acc, c) => {
        acc[c.event_name] = (acc[c.event_name] || 0) + (c.event_count || 0); return acc;
    }, {});
    const totalClicks = gscQueries.reduce((s, q) => s + (q.clicks || 0), 0);
    const totalImpressions = gscQueries.reduce((s, q) => s + (q.impressions || 0), 0);
    const overallCtr = totalImpressions ? ((totalClicks / totalImpressions) * 100).toFixed(2) : "0.00";

    return (
        <div className="space-y-6 px-2 sm:px-0" data-testid="seo-insights-panel">
            {/* Header */}
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#3B82F6] mb-1">SEO Insights</div>
                    <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A]">Search performance & conversions</h2>
                    <p className="text-xs sm:text-sm text-slate-500 mt-1">Live data from Google Analytics 4 and Search Console — cached 10 minutes.</p>
                </div>
                <div className="flex items-center gap-2">
                    <select
                        value={days}
                        onChange={(e) => setDays(Number(e.target.value))}
                        data-testid="seo-days-filter"
                        className="text-sm border border-slate-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-[#1E3A8A] focus:outline-none"
                    >
                        {DAYS_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                    </select>
                    <button
                        onClick={refresh}
                        disabled={refreshing}
                        data-testid="seo-refresh-btn"
                        className="inline-flex items-center gap-1.5 text-sm font-semibold border border-slate-300 px-3 py-2 rounded-lg bg-white hover:bg-slate-50 disabled:opacity-50"
                    >
                        <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
                    </button>
                </div>
            </div>

            <StatusBanner status={status} onConfigure={() => onNavigate?.("settings")} />

            {/* Headline KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="seo-kpis">
                <MetricTile
                    icon={FileSignature}
                    label="Form submits"
                    value={(conversionsByEvent.form_submit || 0).toLocaleString()}
                    sub="Demo registrations + contact"
                    accent="emerald"
                />
                <MetricTile
                    icon={Phone}
                    label="Phone clicks"
                    value={(conversionsByEvent.phone_click || 0).toLocaleString()}
                    sub="tel: link clicks"
                    accent="blue"
                />
                <MetricTile
                    icon={MessageCircle}
                    label="WhatsApp clicks"
                    value={(conversionsByEvent.whatsapp_click || 0).toLocaleString()}
                    sub="wa.me link clicks"
                    accent="emerald"
                />
                <MetricTile
                    icon={Search}
                    label="Search clicks"
                    value={totalClicks.toLocaleString()}
                    sub={`${totalImpressions.toLocaleString()} impressions · ${overallCtr}% CTR`}
                    accent="amber"
                />
            </div>

            {/* Conversion-driving pages */}
            <SectionCard
                eyebrow="Highest leverage"
                title="Pages driving inquiries, calls and WhatsApp clicks"
                testId="seo-conversion-pages"
            >
                {!status?.ga4_configured ? <EmptyState message="GA4 not configured — set it up in Site Settings." /> :
                    loading.ga4 ? <LoadingRow /> :
                    conversions.length === 0 ? <EmptyState /> : (
                        <div className="overflow-x-auto -mx-5">
                            <table className="w-full text-sm min-w-[640px]">
                                <thead className="text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-100">
                                    <tr>
                                        <th className="text-left py-2 px-5">Page</th>
                                        <th className="text-left py-2 px-3">Event</th>
                                        <th className="text-right py-2 px-5">Count</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {conversions.slice(0, 15).map((row, i) => (
                                        <tr key={i} className="hover:bg-slate-50">
                                            <td className="py-2.5 px-5 text-slate-700 truncate max-w-[280px]" title={row.page_path}>{row.page_path}</td>
                                            <td className="py-2.5 px-3">
                                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600 bg-slate-100 px-2 py-0.5 rounded-md">{row.event_name}</span>
                                            </td>
                                            <td className="py-2.5 px-5 text-right font-semibold text-[#0F172A]">{row.event_count.toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )
                }
            </SectionCard>

            {/* Two-column on desktop */}
            <div className="grid lg:grid-cols-2 gap-6">
                {/* Top SEO pages from GSC */}
                <SectionCard eyebrow="Search traffic" title="Top pages on Google" testId="seo-top-pages-gsc">
                    {!status?.gsc_configured ? <EmptyState message="Search Console not configured — set it up in Site Settings." /> :
                        loading.gsc ? <LoadingRow /> :
                        gscPages.length === 0 ? <EmptyState /> : (
                            <ul className="space-y-2">
                                {gscPages.slice(0, 12).map((p, i) => (
                                    <li key={i} className="flex items-start gap-3 p-3 rounded-lg bg-[#F8FAFC] border border-slate-100">
                                        <div className="text-[10px] font-bold uppercase tracking-wider text-[#1E3A8A] bg-white border border-slate-200 px-1.5 py-1 rounded shrink-0">#{i + 1}</div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-xs text-slate-700 truncate" title={p.page}>{p.page.replace(/^https?:\/\/[^/]+/, "")}</div>
                                            <div className="text-[11px] text-slate-500 mt-0.5">
                                                {p.clicks.toLocaleString()} clicks · {p.impressions.toLocaleString()} imp · CTR {p.ctr_pct}% · pos {p.position}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )
                    }
                </SectionCard>

                {/* Top GSC queries */}
                <SectionCard eyebrow="Search keywords" title="Top queries bringing traffic" testId="seo-top-queries-gsc">
                    {!status?.gsc_configured ? <EmptyState message="Search Console not configured." /> :
                        loading.gsc ? <LoadingRow /> :
                        gscQueries.length === 0 ? <EmptyState /> : (
                            <ul className="space-y-2">
                                {gscQueries.slice(0, 12).map((q, i) => (
                                    <li key={i} className="flex items-start gap-3 p-3 rounded-lg bg-[#F8FAFC] border border-slate-100">
                                        <TrendingUp className="w-4 h-4 mt-0.5 text-amber-500 shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium text-[#0F172A] truncate" title={q.query}>{q.query}</div>
                                            <div className="text-[11px] text-slate-500 mt-0.5">
                                                {q.clicks.toLocaleString()} clicks · {q.impressions.toLocaleString()} imp · CTR {q.ctr_pct}% · pos {q.position}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )
                    }
                </SectionCard>
            </div>

            {/* Top GA4 pages + city perf */}
            <div className="grid lg:grid-cols-2 gap-6">
                <SectionCard eyebrow="On-site visits" title="Top pages by sessions (GA4)" testId="seo-top-pages-ga4">
                    {!status?.ga4_configured ? <EmptyState message="GA4 not configured." /> :
                        loading.ga4 ? <LoadingRow /> :
                        topPages.length === 0 ? <EmptyState /> : (
                            <ul className="space-y-2">
                                {topPages.slice(0, 12).map((p, i) => (
                                    <li key={i} className="flex items-start gap-3 p-3 rounded-lg bg-[#F8FAFC] border border-slate-100">
                                        <div className="text-[10px] font-bold uppercase tracking-wider text-[#1E3A8A] bg-white border border-slate-200 px-1.5 py-1 rounded shrink-0">#{i + 1}</div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-xs text-slate-700 truncate" title={p.page_path}>{p.page_path}</div>
                                            <div className="text-[11px] text-slate-500 mt-0.5">
                                                {p.sessions.toLocaleString()} sessions · {p.engaged_sessions.toLocaleString()} engaged · {p.users.toLocaleString()} users
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )
                    }
                </SectionCard>

                {/* City perf */}
                <SectionCard eyebrow="India only" title="City-wise visitor performance" testId="seo-cities">
                    {!status?.ga4_configured ? <EmptyState message="GA4 not configured." /> :
                        loading.ga4 ? <LoadingRow /> :
                        cityStats.length === 0 ? <EmptyState /> : (
                            <ul className="space-y-2">
                                {cityStats.slice(0, 12).map((c, i) => (
                                    <li key={i} className="flex items-center gap-3 p-3 rounded-lg bg-[#F8FAFC] border border-slate-100">
                                        <MapPin className="w-4 h-4 text-[#1E3A8A] shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium text-[#0F172A] truncate">{c.city}</div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className="font-heading font-semibold text-[#0F172A] text-sm">{c.sessions.toLocaleString()}</div>
                                            <div className="text-[10px] text-slate-500">sessions</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )
                    }
                </SectionCard>
            </div>

            {/* Device split */}
            {Object.keys(device).length > 0 && (
                <SectionCard eyebrow="How they visit" title="Mobile vs desktop split" testId="seo-device-split">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {Object.entries(device).map(([d, m]) => (
                            <div key={d} className="p-4 rounded-xl border border-slate-200 bg-[#F8FAFC]">
                                <div className="flex items-center gap-2 mb-2">
                                    {d === "mobile" ? <Smartphone className="w-4 h-4 text-[#1E3A8A]" /> : <Globe2 className="w-4 h-4 text-[#1E3A8A]" />}
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-600">{d}</div>
                                </div>
                                <div className="font-heading font-bold text-2xl text-[#0F172A]">{(m.sessions || 0).toLocaleString()}</div>
                                <div className="text-xs text-slate-500 mt-1">sessions · bounce {((m.bounce_rate || 0) * 100).toFixed(1)}%</div>
                            </div>
                        ))}
                    </div>
                </SectionCard>
            )}

            <p className="text-[11px] text-slate-400 text-center">
                Data refreshes every 10 minutes from Google APIs · <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-0.5">Open Search Console <ExternalLink className="w-3 h-3" /></a>
            </p>
        </div>
    );
}
